import { createPlatformWorkerManager } from '@wix/editor-platform-worker';
import type {
  DocumentServices,
  HostAPI,
} from '@wix/editor-platform-private-api/dist/types';
import {
  HostAPIClassic,
  HostAPIEditorX,
  HostAPIStudio,
  HostType,
} from '@wix/editor-platform-private-api';
import {
  createEditorPlatformInternalError,
  EditorPlatformInternalErrorCode,
} from '@wix/public-editor-platform-errors';
import { PlatformApiMethods } from '../../types/platformApi';
import { IExperimentsApiAdapter } from '../utils/experimentsApiAdapter';
import { EditorPointerAPI } from '@wix/editorx-core-api';

const getHostAPI = <THostType extends HostType>(
  hostType: THostType,
  platformApiMethods: PlatformApiMethods,
): HostAPI => {
  switch (hostType) {
    case HostType.Classic:
      return new HostAPIClassic(platformApiMethods) as unknown as HostAPI;
    case HostType.Editorx:
      return new HostAPIEditorX(platformApiMethods) as unknown as HostAPI;
    case HostType.Studio:
      return new HostAPIStudio(platformApiMethods) as unknown as HostAPI;
  }

  throw createEditorPlatformInternalError(
    EditorPlatformInternalErrorCode.UnexpectedError,
  ).withHost(hostType);
};

export async function initNewPlatform({
  host,
  __temporaryPlatformApiMethods,
  documentServicesAPI,
  experimentsAPI,
  editorPointerAPI,
}: {
  host: HostType;
  documentServicesAPI: DocumentServices;
  experimentsAPI: IExperimentsApiAdapter;
  __temporaryPlatformApiMethods: PlatformApiMethods;
  editorPointerAPI?: EditorPointerAPI;
}) {
  const ALLOWED_HOSTS = [HostType.Classic, HostType.Editorx, HostType.Studio];

  if (!ALLOWED_HOSTS.includes(host)) {
    return;
  }

  if (!Object.values(HostType).includes(host)) {
    throw createEditorPlatformInternalError(
      EditorPlatformInternalErrorCode.UnexpectedError,
      `Could not determine host type`,
    ).withHost(host);
  }

  /**
   * TODO:
   * - align types
   */
  return createPlatformWorkerManager(host, {
    hostAPI: getHostAPI(host, __temporaryPlatformApiMethods),
    documentServicesAPI,
    experimentsAPI,
    editorPointerAPI,
  });
}
